import { useInfiniteQuery } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';

import {
  listLibraryTrackCards,
  type LibraryTrackCardQuery,
  type TrackCard,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { Button } from '../../design-system/button.js';
import { Select } from '../../design-system/select.js';
import { StateSurface } from '../../design-system/surfaces.js';
import { usePlaybackTransport } from '../../player/index.js';
import {
  TrackCardSurface,
  TrackRow,
  playbackItemFromTrack,
  useTrackPlaybackAction,
} from '../tracks/track-presentation.js';
import { PageHeader } from '../shared/page.js';

const importStatuses = [
  'any',
  'pending',
  'materializing',
  'available',
  'failed',
  'removed',
] as const;

function LibraryTrack({
  track,
  onStart,
}: {
  track: TrackCard;
  onStart: () => Promise<void> | void;
}) {
  const playback = useTrackPlaybackAction(track, onStart);
  const trailing = (
    <Button asChild aria-label="Open track details" size="icon" variant="ghost">
      <Link to={`/tracks/${track.id}`}>
        <MoreHorizontal aria-hidden="true" />
      </Link>
    </Button>
  );
  return (
    <>
      <TrackRow action={playback} track={track} trailing={trailing} />
      <TrackCardSurface action={playback} track={track} trailing={trailing} />
    </>
  );
}

export function LibraryPage() {
  const { library } = useWorkspace();
  const player = usePlaybackTransport();
  const [params, setParams] = useSearchParams();
  const statusParam = params.get('status');
  const status = importStatuses.includes(statusParam as (typeof importStatuses)[number])
    ? (statusParam as (typeof importStatuses)[number])
    : 'any';
  const baseQuery = useMemo<LibraryTrackCardQuery>(
    () => ({ limit: 50, ...(status === 'any' ? {} : { status }) }),
    [status],
  );
  const tracksQuery = useInfiniteQuery({
    queryKey: ['library-tracks', library.id, baseQuery],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listLibraryTrackCards(library.id, {
        ...baseQuery,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    refetchInterval: ({ state }) =>
      state.data?.pages.some((page) =>
        page.items.some((track) =>
          ['pending', 'materializing'].includes(track.library_import?.status ?? ''),
        ),
      )
        ? 5_000
        : false,
  });
  const tracks = tracksQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const playbackItems = useMemo(
    () => tracks.flatMap((track) => playbackItemFromTrack(track) ?? []),
    [tracks],
  );
  const playbackContext = useMemo(
    () => ({
      kind: 'library' as const,
      id: `library:${library.id}:${status}`,
      label: 'Library',
    }),
    [library.id, status],
  );

  const startLibraryPlayback = async (trackId: string) => {
    const startIndex = playbackItems.findIndex((item) => item.id === trackId);
    if (startIndex < 0) return;
    await player.playQueue(playbackItems, startIndex, playbackContext);
    const completeTracks = [...tracks];
    const seenCursors = new Set<string>();
    let cursor = tracksQuery.data?.pages.at(-1)?.next_cursor ?? null;
    try {
      while (cursor && !seenCursors.has(cursor)) {
        seenCursors.add(cursor);
        const page = await listLibraryTrackCards(library.id, { ...baseQuery, cursor });
        completeTracks.push(...page.items);
        cursor = page.next_cursor;
      }
    } catch {
      return;
    }
    const completeItems = completeTracks.flatMap((track) => playbackItemFromTrack(track) ?? []);
    player.appendQueue(playbackContext.id, completeItems);
  };

  return (
    <section>
      <PageHeader
        title="Library"
        description="Tracks saved as full-quality WAV files."
        action={
          <div className="w-full sm:w-56">
            <Select
              aria-label="Import status filter"
              options={importStatuses.map((value) => ({
                value,
                label: value === 'any' ? 'Any import status' : value.replaceAll('_', ' '),
              }))}
              value={status}
              onValueChange={(value) => {
                const next = new URLSearchParams(params);
                if (value === 'any') next.delete('status');
                else next.set('status', value);
                setParams(next, { replace: true });
              }}
            />
          </div>
        }
      />

      <div className="mt-6">
        {tracksQuery.isPending ? (
          <StateSurface kind="loading" title="Loading Library" />
        ) : tracksQuery.isError ? (
          <StateSurface
            action={
              <Button onClick={() => void tracksQuery.refetch()} size="small" variant="secondary">
                Try again
              </Button>
            }
            kind="error"
            title="Library could not be loaded"
          />
        ) : tracks.length === 0 ? (
          <StateSurface
            action={
              status === 'any' ? (
                <Button asChild size="small" variant="secondary">
                  <Link to="/catalog">Browse Catalog</Link>
                </Button>
              ) : undefined
            }
            title={status === 'any' ? 'Your Library is empty' : 'No imports match this status'}
          >
            {status === 'any'
              ? 'Use Download WAV from a Catalog track menu to save it here.'
              : 'Choose another import status to review different tracks.'}
          </StateSurface>
        ) : (
          <>
            <div className="hidden grid-cols-[2.75rem_minmax(12rem,1fr)_minmax(14rem,0.8fr)_auto] gap-4 rounded-t-md border border-border bg-bg-2 px-4 py-2 font-mono text-xs uppercase tracking-[0.1em] text-ink-3 md:grid">
              <span aria-hidden="true" />
              <span>Track</span>
              <span>Import and delivery</span>
              <span aria-hidden="true" />
            </div>
            <div className="grid gap-3 md:block md:overflow-hidden md:rounded-b-md md:border-x md:border-b md:border-border">
              {tracks.map((track) => (
                <LibraryTrack
                  key={track.id}
                  track={track}
                  onStart={() => startLibraryPlayback(track.id)}
                />
              ))}
            </div>
            {tracksQuery.hasNextPage ? (
              <div className="mt-5 flex justify-center">
                <Button
                  disabled={tracksQuery.isFetchingNextPage}
                  onClick={() => void tracksQuery.fetchNextPage()}
                  variant="secondary"
                >
                  {tracksQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
